import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useIsCustomerMode } from '@plato/app-core/hooks/use-customer-mode';
import type { ConfirmationDialogProps } from '@plato/ui-legacy/components/dialog/confirmation-dialog';
import { ConfirmationDialog } from '@plato/ui-legacy/components/dialog/confirmation-dialog';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import { format } from 'date-fns';
import { DownloadIcon, InfoIcon } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAsyncFn } from 'react-use';
import * as XLSX from 'xlsx';
import type { TargetUnit, VisibilityWindow } from '../../../server/utils/explorer-utils.ts';
import {
    ExplorerTopLevelEntity,
    getDateRangeOrThrow,
    shouldHideUnselectedRows,
} from '../../../server/utils/explorer-utils.ts';
import { api } from '../../../utils/api.ts';
import { safeParseBreadcrumbs } from '../../../utils/insights-utils.ts';
import {
    useConfigurableExplorerColumns,
    type ExplorerTableData,
    type XlsxCellValue,
} from '../hooks/use-configurable-explorer-columns.tsx';
import { useExplorerView } from '../hooks/use-explorer-query-params.ts';
import { getExplorerTabLabel } from '../utils/explorer-tab-labels.ts';

type UseColumnsProps = {
    tab: ExplorerTopLevelEntity;
    targetUnit: TargetUnit;
    window: VisibilityWindow;
    yearPeriods: number;
};

type ExportColumn = {
    id: string;
    name: string;
    getXlsxCell: (row: ExplorerTableData) => XlsxCellValue;
};

function useColumns({ tab }: UseColumnsProps): ExportColumn[] {
    const { t } = useTranslation('explorer');

    const { breadcrumbDepthsToHide } = useAppConfig();
    const customerViewToggled = useIsCustomerMode();

    const explorerColumns = useConfigurableExplorerColumns({
        columns: [],
    });

    const exporterColumns: ExportColumn[] = explorerColumns
        .flatMap((col) => (col.exporter ? col.exporter.map((exporter) => ({ ...col, exporter })) : []))
        .map((col) => ({
            id: col.id,
            name: col.exporter.name,
            getXlsxCell: col.exporter.getXlsxCell,
        }));

    const columns: ExportColumn[] = [
        {
            id: 'entity_id',
            name: 'ID',
            getXlsxCell: (row: ExplorerTableData) => row.id ?? '',
        },
        {
            id: 'name',
            name: getExplorerTabLabel(t, tab),
            getXlsxCell: (row: ExplorerTableData) => {
                const name = row.name ?? '';
                if (!('breadcrumbs' in row) || typeof row.breadcrumbs !== 'string') {
                    return name;
                }
                const breadcrumbs = safeParseBreadcrumbs(row.breadcrumbs)?.slice(breadcrumbDepthsToHide);
                if (!breadcrumbs || breadcrumbs.length === 0) {
                    return name;
                }
                return [...breadcrumbs.map((breadcrumb) => breadcrumb.title), name].join(' / ');
            },
        },
        ...exporterColumns,
    ]
        // We don't want to export the margin columns in customer view
        .filter((col) => {
            if (customerViewToggled) {
                return col.id.includes('margin') ? false : true;
            }
            return true;
        });

    return columns;
}

export function ExplorerExportDataButton(): ReactNode {
    const { state, defaultFilters, combinedFilters } = useExplorerView();
    const utils = api.useUtils();

    const { t } = useTranslation('explorer');
    const { toast } = useToast();
    const { normalisedCategoryDepthsUsedInInsightsInExplorer } = useAppConfig();
    const { tab, searchString, window, customDateRange, billingStatus, sorting, targetUnit, yearPeriods } =
        state;

    const columns = useColumns({ tab, window, targetUnit, yearPeriods });
    const dateRange = getDateRangeOrThrow(
        window,
        customDateRange ? { from: new Date(customDateRange.from), to: new Date(customDateRange.to) } : null,
    );

    const [explorerDataState, fetchData] = useAsyncFn(async () => {
        const allData: ExplorerTableData[] = [];
        const limit = 5000;
        const queryInput = {
            hideUnselectedRows: shouldHideUnselectedRows({
                tab,
                defaultFilters,
            }),
            searchString,
            filters: combinedFilters,
            customDateRange,
            window,
            billingStatus,
            sorting,
            yearPeriods,
            limit,
        };
        type ExportQuery = (input: typeof queryInput & { cursor: number | null }) => Promise<{
            result: ExplorerTableData[];
            nextCursor?: number | null;
        }>;
        const explorerClient = utils.client.platoUser.query.explorer;
        // Exhaustive over ExplorerTopLevelEntity so a new tab fails typecheck
        // here instead of silently breaking the export.
        const callExplorerApi: Record<ExplorerTopLevelEntity, ExportQuery | undefined> = {
            [ExplorerTopLevelEntity.CLERK]: explorerClient.getForClerk.getListWithFilters.query,
            [ExplorerTopLevelEntity.CUSTOMER]: explorerClient.getForCustomer.getListWithFilters.query,
            [ExplorerTopLevelEntity.INDUSTRY]: explorerClient.getForIndustry.getListWithFilters.query,
            [ExplorerTopLevelEntity.PRODUCT]: explorerClient.getForProduct.getListWithFilters.query,
            [ExplorerTopLevelEntity.PRODUCT_GROUP]:
                explorerClient.getForProductGroup.getListWithFilters.query,
            [ExplorerTopLevelEntity.SALES_REP]: explorerClient.getForSalesRep.getListWithFilters.query,
            [ExplorerTopLevelEntity.STORAGE_LOCATION]:
                explorerClient.getForStorageLocation.getListWithFilters.query,
            [ExplorerTopLevelEntity.SUPPLIER]: explorerClient.getForSupplier.getListWithFilters.query,
            [ExplorerTopLevelEntity.NORMALISED_CATEGORY]:
                normalisedCategoryDepthsUsedInInsightsInExplorer == null
                    ? undefined
                    : (input) =>
                          explorerClient.getForNormalisedCategory.getListWithFilters.query({
                              ...input,
                              normalisedCategoryDepth: normalisedCategoryDepthsUsedInInsightsInExplorer,
                          }),
        };
        const queryTab = tab in callExplorerApi ? callExplorerApi[tab] : undefined;

        if (!queryTab) {
            return allData;
        }

        const fetch = async (cursor: number | null): ReturnType<ExportQuery> =>
            queryTab({ ...queryInput, cursor });

        const now = new Date();
        const response = await fetch(null);
        allData.push(...response.result);
        let nextCursor = response.nextCursor;

        // Fetch two pages at a time to avoid too many requests
        while (nextCursor) {
            const allResponse = await Promise.all([
                fetch(nextCursor),
                nextCursor
                    ? fetch(nextCursor + limit)
                    : Promise.resolve({ result: [], nextCursor: undefined }),
            ]);

            const [nextResponse, nextNextResponse] = allResponse;

            allData.push(...nextResponse.result, ...nextNextResponse.result);
            nextCursor = nextNextResponse.nextCursor;
        }

        const endDate = new Date();

        console.debug(
            'Duration of data fetching',
            `Start: ${format(now, 'yyyy-MM-dd HH:mm:ss')}`,
            `End: ${format(endDate, 'yyyy-MM-dd HH:mm:ss')}`,
            `Duration: ${Math.round((endDate.getTime() - now.getTime()) / 1000)} seconds`,
        );

        return allData;
    }, [
        tab,
        yearPeriods,
        normalisedCategoryDepthsUsedInInsightsInExplorer,
        dateRange.to.toISOString(),
        dateRange.from.toISOString(),
        billingStatus,
        searchString,
        sorting.direction,
        sorting.field,
        combinedFilters,
        defaultFilters,
    ]);

    const { value: data, loading: isLoading, error } = explorerDataState;

    const exportFile = (exportData: ExplorerTableData[]): void => {
        const hasDistinctId = exportData.some(
            (row) => row.id != null && row.id !== '' && row.id !== row.name,
        );
        const effectiveColumns = hasDistinctId ? columns : columns.filter((col) => col.id !== 'entity_id');

        const headers = effectiveColumns.map((col) => col.name);
        const rows = exportData.map((row) => effectiveColumns.map((col) => col.getXlsxCell(row) ?? ''));

        // Create worksheet using aoa_to_sheet (array of arrays)
        const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Export');

        // Download file
        const filename = `Plato_Export_${format(dateRange.from, 'yyyy-MM-dd')}-${format(dateRange.to, 'yyyy-MM-dd')}.xlsx`;
        XLSX.writeFile(workbook, filename);
    };

    useEffect(() => {
        if (data && !isLoading && data.length > 0) {
            exportFile(data);
        } else if (data && data.length === 0) {
            toast({
                icon: 'info',
                title: t('explorer__export_no_data_title'),
                description: t('explorer__export_no_data_description'),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, isLoading]);

    useEffect(() => {
        if (error) {
            toast({
                icon: 'warning',
                title: t('explorer__export_failed_title'),
                description: t('explorer__export_failed_description'),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);

    const openConfirmationDialog = (): void => {
        void DialogStateManager.show(ConfirmationDialog, {
            id: 'export-confirmation-dialog',
            focus: 'submit',
            icon: InfoIcon,
            title: t('explorer__export_confirmation_title'),
            description: (
                <div className="flex flex-col">
                    <ul className="list-disc text-left px-4">
                        <li>{<Trans t={t} i18nKey="explorer__export_confirmation_content_1" />}</li>
                        <li>{<Trans t={t} i18nKey="explorer__export_confirmation_content_2" />}</li>
                        <li>{<Trans t={t} i18nKey="explorer__export_confirmation_content_3" />}</li>
                    </ul>
                </div>
            ),
            accept: {
                text: t('explorer__export_confirmation_accept'),
                action: async () => {
                    await fetchData();
                },
            },
            reject: {
                text: t('form__cancel', {
                    ns: 'common',
                }),
            },
        } satisfies ConfirmationDialogProps);
    };

    return (
        <Button
            variant="subtle"
            icon={DownloadIcon}
            onClick={openConfirmationDialog}
            isLoading={isLoading}
            isDisabled={isLoading}
        >
            {t('explorer__export')}
        </Button>
    );
}
